/**
 * CSS graphics pipeline — author a graph of graphic operators, compile to CSS.
 * This is not an FX catalog. Operators are paint tools; every brief gets a unique sheet.
 */

import { slugifyId } from "./motion-spec";

function normalizePrompt(input: string): string {
  return input
    .toLowerCase()
    .replace(/[_/\\|+]+/g, " ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function hasTerm(text: string, term: string): boolean {
  if (!term) return false;
  if (term.includes(" ")) return text.includes(term);
  const re = new RegExp(`(?:^|[^a-z0-9])${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:[^a-z0-9]|$)`);
  return re.test(text);
}

export const CSS_GRAPH_SCHEMA = "forge.cssgraph.v1" as const;

export type FlowAxis = "x" | "y" | "diag";
export type EmitPrim = "dot" | "dash" | "diamond" | "char";
export type LatticeKind = "square" | "hex" | "diag";
export type FillMode = "linear" | "radial" | "conic";
export type Dir = "up" | "down" | "left" | "right" | "drift";

export type CssOp =
  | { op: "fill"; mode: FillMode; angle: number; speed: number; gain: number }
  | { op: "veil"; blobs: number; blur: number; speed: number }
  | { op: "flow"; axis: FlowAxis; gap: number; thick: number; speed: number }
  | { op: "emit"; prim: EmitPrim; count: number; dir: Dir; size: number; speed: number }
  | { op: "lattice"; kind: LatticeKind; scale: number; speed: number }
  | { op: "burst"; spokes: number; speed: number }
  | { op: "ripple"; count: number; speed: number }
  | { op: "type"; alphabet: string; count: number; dir: Dir }
  | { op: "grain"; density: number }
  | { op: "pulse"; amount: number; speed: number };

export interface CssGraph {
  schema: typeof CSS_GRAPH_SCHEMA;
  seed: number;
  ops: CssOp[];
  features: string[];
}

export function graphFromPlan(
  ops: CssOp[],
  extra?: { seed?: number; features?: string[] },
): CssGraph {
  return {
    schema: CSS_GRAPH_SCHEMA,
    seed: extra?.seed ?? Date.now() >>> 0,
    ops: ops.slice(0, 4),
    features: extra?.features?.length ? extra.features : ["generate"],
  };
}

export interface VisualFeatures {
  wet: number;
  heat: number;
  digital: number;
  soft: number;
  structured: number;
  organic: number;
  dir: Dir;
  freq: number;
  tempo: number;
  still: boolean;
  seed: number;
  letters: string;
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function u(seed: number, salt: number): number {
  return ((Math.imul(seed ^ (salt * 374761393), 668265263) >>> 8) & 255) / 255;
}

function mix(seed: number, a: number, b: number, salt: number): number {
  return a + (b - a) * u(seed, salt);
}

function any(text: string, terms: string[]): boolean {
  return terms.some((t) => hasTerm(text, t));
}

function score(text: string, terms: Array<[string, number]>): number {
  let s = 0;
  for (const [t, w] of terms) if (hasTerm(text, t)) s += w;
  return Math.max(0, Math.min(1, s / 14));
}

export function analyzeVisual(prompt: string): VisualFeatures {
  const text = normalizePrompt((prompt || "").slice(0, 2000)).replace(/bianary|bianry/g, "binary");
  const seed = hashSeed(text || "0");
  const still =
    any(text, ["no motion", "no animation", "disable motion", "solid background"]) ||
    (hasTerm(text, "still") && !hasTerm(text, "distill"));

  let dir: Dir = "drift";
  if (any(text, ["waterfall", "falling", "fall", "downpour", "rainfall", "rain"])) dir = "down";
  else if (any(text, ["rising", "rise", "upward", "ember"])) dir = "up";
  else if (any(text, ["from the right", "to the left"])) dir = "left";
  else if (any(text, ["from the left", "to the right", "horizontal", "across", "flowing", "stream", "streams", "current"])) {
    dir = "right";
  }

  const letters = text
    .split(" ")
    .filter((w) => w.length > 2)
    .map((w) => w[0]!.toUpperCase())
    .join("")
    .slice(0, 8);

  return {
    wet: score(text, [
      ["water", 6], ["ocean", 5], ["river", 5], ["ink", 6], ["bleed", 5],
      ["rain", 4], ["tide", 4], ["aqua", 4], ["liquid", 5], ["blot", 5], ["soak", 4],
    ]),
    heat: score(text, [
      ["fire", 6], ["flame", 6], ["ember", 6], ["lava", 5], ["magma", 5],
      ["sun", 4], ["heat", 4], ["burning", 5], ["inferno", 6],
    ]),
    digital: score(text, [
      ["binary", 7], ["matrix", 6], ["code", 5], ["grid", 5], ["terminal", 4],
      ["cyber", 4], ["digit", 5], ["scan", 3],
    ]),
    soft: score(text, [
      ["fog", 6], ["mist", 5], ["velvet", 5], ["dust", 5], ["cloud", 4],
      ["haze", 5], ["soft", 4], ["pollen", 4], ["silk", 4],
    ]),
    structured: score(text, [
      ["grid", 6], ["hex", 5], ["clockwork", 6], ["crystal", 5], ["lattice", 6],
      ["wireframe", 5], ["gear", 4], ["honeycomb", 5],
    ]),
    organic: score(text, [
      ["moss", 5], ["leaf", 4], ["beetle", 5], ["vine", 4], ["parchment", 4],
      ["bloom", 4], ["root", 3], ["forest", 4],
    ]),
    dir,
    freq: any(text, ["dense", "heavy", "many", "thick"])
      ? 0.85
      : any(text, ["sparse", "few", "thin", "light"])
        ? 0.3
        : mix(seed, 0.4, 0.75, 2),
    tempo: any(text, ["slow", "lazy", "gentle", "cathedral"])
      ? 0.28
      : any(text, ["fast", "frantic", "rapid"])
        ? 0.85
        : mix(seed, 0.35, 0.65, 3),
    still,
    seed,
    letters,
  };
}

export function authorCssGraph(prompt: string): CssGraph {
  const f = analyzeVisual(prompt);
  const ops: CssOp[] = [];
  const features: string[] = [];
  const { seed } = f;

  if (f.still) {
    return { schema: CSS_GRAPH_SCHEMA, seed, ops: [], features: ["still"] };
  }

  if (f.wet > 0.28) {
    features.push(`wet:${f.wet.toFixed(2)}`);
    ops.push({
      op: "flow",
      axis: f.dir === "down" || f.dir === "up" ? "y" : f.dir === "drift" ? "diag" : "x",
      gap: mix(seed, 14, 36, 4),
      thick: mix(seed, 1.5, 5, 5),
      speed: f.tempo,
    });
    if (f.wet > 0.45) {
      ops.push({ op: "veil", blobs: 2 + Math.round(f.freq * 2), blur: mix(seed, 16, 34, 6), speed: f.tempo * 0.6 });
    }
    ops.push({
      op: "type",
      alphabet: f.digital > 0.35 ? "01" : "~≈∽",
      count: Math.round(16 + f.freq * 24),
      dir: f.dir === "drift" ? "right" : f.dir,
    });
  }
  if (f.heat > 0.28) {
    features.push(`heat:${f.heat.toFixed(2)}`);
    ops.push({
      op: "emit",
      prim: "diamond",
      count: Math.round(12 + f.freq * 22),
      dir: "up",
      size: mix(seed, 4, 9, 7),
      speed: f.tempo,
    });
    ops.push({ op: "pulse", amount: mix(seed, 0.25, 0.55, 8), speed: f.tempo });
    ops.push({ op: "fill", mode: "radial", angle: 0, speed: f.tempo * 0.5, gain: mix(seed, 0.3, 0.55, 9) });
  }
  if (f.digital > 0.28) {
    features.push(`digital:${f.digital.toFixed(2)}`);
    ops.push({
      op: "lattice",
      kind: f.structured > 0.4 ? "hex" : "square",
      scale: mix(seed, 18, 36, 10),
      speed: f.tempo,
    });
    if (!ops.some((o) => o.op === "type")) {
      ops.push({ op: "type", alphabet: "01", count: Math.round(20 + f.freq * 22), dir: "down" });
    }
    ops.push({
      op: "emit",
      prim: "dash",
      count: Math.round(14 + f.freq * 20),
      dir: "down",
      size: mix(seed, 10, 28, 11),
      speed: 0.55 + f.tempo * 0.4,
    });
  }
  if (f.soft > 0.28) {
    features.push(`soft:${f.soft.toFixed(2)}`);
    ops.push({ op: "veil", blobs: 3, blur: mix(seed, 22, 40, 12), speed: f.tempo * 0.5 });
    ops.push({ op: "grain", density: f.freq });
    ops.push({
      op: "emit",
      prim: "dot",
      count: Math.round(18 + f.freq * 20),
      dir: f.dir,
      size: mix(seed, 3, 7, 13),
      speed: f.tempo * 0.7,
    });
  }
  if (f.structured > 0.3 && !ops.some((o) => o.op === "lattice")) {
    features.push(`struct:${f.structured.toFixed(2)}`);
    ops.push({
      op: "lattice",
      kind: u(seed, 14) > 0.5 ? "hex" : "diag",
      scale: mix(seed, 16, 32, 15),
      speed: f.tempo * 0.6,
    });
  }
  if (f.organic > 0.28) {
    features.push(`organic:${f.organic.toFixed(2)}`);
    ops.push({
      op: "fill",
      mode: u(seed, 16) > 0.5 ? "radial" : "conic",
      angle: mix(seed, 20, 160, 17),
      speed: f.tempo * 0.45,
      gain: mix(seed, 0.25, 0.5, 18),
    });
    ops.push({ op: "ripple", count: 3 + Math.round(f.freq * 3), speed: f.tempo * 0.5 });
  }

  if (!ops.length) {
    features.push("seeded");
    const bag: CssOp[] = [
      { op: "fill", mode: (["linear", "radial", "conic"] as const)[seed % 3]!, angle: mix(seed, 15, 170, 20), speed: f.tempo, gain: mix(seed, 0.28, 0.52, 21) },
      { op: "flow", axis: (["x", "y", "diag"] as const)[Math.floor(u(seed, 22) * 3)]!, gap: mix(seed, 16, 40, 23), thick: mix(seed, 2, 6, 24), speed: f.tempo },
      { op: "emit", prim: (["dot", "dash", "diamond"] as const)[Math.floor(u(seed, 25) * 3)]!, count: Math.round(14 + f.freq * 22), dir: f.dir, size: mix(seed, 4, 12, 26), speed: f.tempo },
      { op: "veil", blobs: 2 + (seed % 2), blur: mix(seed, 14, 30, 27), speed: f.tempo * 0.6 },
      { op: "burst", spokes: 6 + (seed % 7), speed: f.tempo * 0.4 },
      { op: "type", alphabet: f.letters || "·+", count: 16 + Math.round(f.freq * 14), dir: f.dir },
    ];
    const n = 2 + (seed % 2);
    const start = seed % bag.length;
    for (let i = 0; i < n; i++) ops.push(bag[(start + i) % bag.length]!);
  }

  // Unique gain/count jitter so two similar briefs still differ
  const uniq = ops.slice(0, 4).map((op, i) => jitterOp(op, seed, i));
  return { schema: CSS_GRAPH_SCHEMA, seed, ops: uniq, features };
}

function jitterOp(op: CssOp, seed: number, i: number): CssOp {
  const j = mix(seed, 0.92, 1.08, 30 + i);
  if (op.op === "emit") return { ...op, count: Math.max(8, Math.round(op.count * j)), size: op.size * j };
  if (op.op === "type") return { ...op, count: Math.max(8, Math.round(op.count * j)) };
  if (op.op === "flow") return { ...op, gap: op.gap * j, thick: op.thick * j };
  if (op.op === "fill") return { ...op, angle: (op.angle + mix(seed, -18, 18, 40 + i) + 360) % 360 };
  return op;
}

export function cssEscapeIdent(id: string): string {
  return String(id || "fx")
    .replace(/[^a-zA-Z0-9_-]/g, "-")
    .replace(/^-+/, "x")
    .slice(0, 48);
}

function tokenA() {
  return "var(--pf-purple, #a78bfa)";
}
function tokenB() {
  return "var(--pf-cyan, #22d3ee)";
}

export function compileCssGraph(graph: CssGraph, selector: string): string {
  const sel = selector;
  const parts = [
    `${sel} .sf-motion-host,${sel}.sf-motion-host{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:1}`,
  ];
  graph.ops.forEach((op, i) => {
    const n = i + 1;
    const dur = (2.2 + (1.1 - ("speed" in op ? op.speed : 0.5)) * 6).toFixed(2);
    if (op.op === "fill") {
      const g =
        op.mode === "conic"
          ? `conic-gradient(from ${op.angle.toFixed(0)}deg, color-mix(in srgb,${tokenA()} ${Math.round(op.gain * 70)}%, transparent), color-mix(in srgb,${tokenB()} ${Math.round(op.gain * 50)}%, transparent), transparent)`
          : op.mode === "radial"
            ? `radial-gradient(ellipse at ${30 + (graph.seed % 40)}% ${40 + (graph.seed % 30)}%, color-mix(in srgb,${tokenA()} ${Math.round(op.gain * 70)}%, transparent), transparent 62%)`
            : `linear-gradient(${op.angle.toFixed(0)}deg, color-mix(in srgb,${tokenA()} ${Math.round(op.gain * 55)}%, transparent), color-mix(in srgb,${tokenB()} ${Math.round(op.gain * 40)}%, transparent), transparent)`;
      parts.push(
        `${sel} .sf-g-fill-${n}{position:absolute;inset:-18%;background:${g};background-size:180% 180%;opacity:.9;animation:sfg-mesh ${dur}s ease-in-out infinite;pointer-events:none;z-index:0}`,
      );
    }
    if (op.op === "veil") {
      parts.push(
        `${sel} .sf-g-veil-${n}{position:absolute;inset:-22%;filter:blur(${op.blur.toFixed(0)}px);opacity:.72;background:radial-gradient(ellipse at 28% 68%,color-mix(in srgb,${tokenA()} 34%,transparent),transparent 55%),radial-gradient(ellipse at 74% 32%,color-mix(in srgb,${tokenB()} 30%,transparent),transparent 52%);animation:sfg-drift ${dur}s ease-in-out infinite alternate;pointer-events:none;z-index:1}`,
      );
    }
    if (op.op === "flow") {
      const ang = op.axis === "x" ? 90 : op.axis === "y" ? 180 : 128;
      const name = op.axis === "y" ? "sfg-scroll-y" : "sfg-scroll-x";
      parts.push(
        `${sel} .sf-g-flow-${n}{position:absolute;inset:-20%;opacity:.88;background:repeating-linear-gradient(${ang}deg,transparent,transparent ${op.gap.toFixed(1)}px,color-mix(in srgb,${tokenB()} 42%,${tokenA()}) ${op.gap.toFixed(1)}px,color-mix(in srgb,${tokenA()} 28%,transparent) ${(op.gap + op.thick).toFixed(1)}px);animation:${name} ${dur}s linear infinite;pointer-events:none;z-index:2}`,
      );
    }
    if (op.op === "emit") {
      const anim = op.dir === "up" ? "sfg-rise" : op.dir === "left" ? "sfg-stream-l" : op.dir === "right" ? "sfg-stream-r" : op.dir === "down" ? "sfg-fall" : "sfg-drift";
      const shape =
        op.prim === "dash"
          ? `width:2px;height:${op.size.toFixed(0)}px;border-radius:2px;`
          : op.prim === "diamond"
            ? `width:${op.size.toFixed(0)}px;height:${op.size.toFixed(0)}px;transform:rotate(45deg);`
            : `width:${op.size.toFixed(0)}px;height:${op.size.toFixed(0)}px;border-radius:50%;`;
      parts.push(
        `${sel} .sf-g-emit-${n}{position:absolute;${shape}background:color-mix(in srgb,${tokenA()} 40%,#fff);box-shadow:0 0 10px ${tokenA()};animation:${anim} ${dur}s linear infinite;pointer-events:none;z-index:3}`,
      );
    }
    if (op.op === "lattice") {
      const size = op.scale.toFixed(0);
      const line = `color-mix(in srgb,${tokenA()} 38%, transparent)`;
      const bg =
        op.kind === "hex"
          ? `radial-gradient(circle, transparent 38%, ${line} 40%, transparent 42%)`
          : op.kind === "diag"
            ? `repeating-linear-gradient(45deg, transparent, transparent 12px, ${line} 12px, ${line} 13px)`
            : `linear-gradient(${line} 1px, transparent 1px),linear-gradient(90deg, ${line} 1px, transparent 1px)`;
      const bsz = op.kind === "square" ? `${size}px ${size}px` : `${size}px ${size}px`;
      parts.push(
        `${sel} .sf-g-lat-${n}{position:absolute;inset:-8%;opacity:.65;background-image:${bg};background-size:${bsz};animation:sfg-scroll-y ${dur}s linear infinite;pointer-events:none;z-index:1}`,
      );
    }
    if (op.op === "burst") {
      parts.push(
        `${sel} .sf-g-burst-${n}{position:absolute;inset:-25%;opacity:.5;background:repeating-conic-gradient(from 0deg at 50% 110%,transparent 0 10deg,color-mix(in srgb,${tokenA()} 32%,transparent) 10deg 14deg);animation:sfg-spin ${dur}s linear infinite;pointer-events:none;z-index:1}`,
      );
    }
    if (op.op === "ripple") {
      parts.push(
        `${sel} .sf-g-rip-${n}{position:absolute;left:50%;top:50%;border:2px solid color-mix(in srgb,${tokenB()} 55%,transparent);border-radius:50%;animation:sfg-ring ${dur}s ease-out infinite;pointer-events:none;z-index:2}`,
      );
    }
    if (op.op === "type") {
      const anim = op.dir === "left" || op.dir === "right" ? "sfg-stream-r" : op.dir === "up" ? "sfg-rise" : "sfg-fall";
      parts.push(
        `${sel} .sf-fx-glyph{position:absolute;z-index:4;font-family:ui-monospace,Consolas,monospace;font-weight:700;color:color-mix(in srgb,${tokenB()} 50%,#ecfeff);text-shadow:0 0 10px ${tokenA()};animation:${anim} ${dur}s linear infinite;pointer-events:none}`,
      );
    }
    if (op.op === "grain") {
      parts.push(
        `${sel} .sf-g-grain-${n}{position:absolute;inset:0;opacity:${(0.12 + op.density * 0.18).toFixed(2)};background-image:radial-gradient(color-mix(in srgb,${tokenA()} 70%,#fff) 0.7px, transparent 0.8px);background-size:4px 4px;pointer-events:none;z-index:2}`,
      );
    }
    if (op.op === "pulse") {
      parts.push(
        `${sel} .sf-g-pulse-${n}{position:absolute;inset:12%;border-radius:50%;background:radial-gradient(circle,color-mix(in srgb,${tokenA()} ${Math.round(op.amount * 80)}%,transparent),transparent 70%);animation:sfg-pulse ${dur}s ease-in-out infinite;pointer-events:none;z-index:1}`,
      );
    }
  });
  parts.push(`@keyframes sfg-drift{from{transform:translate(0,0) scale(1)}to{transform:translate(7%,-5%) scale(1.08)}}
@keyframes sfg-mesh{0%{background-position:0% 40%}50%{background-position:100% 60%}100%{background-position:0% 40%}}
@keyframes sfg-fall{0%{transform:translateY(0);opacity:0}12%{opacity:.9}100%{transform:translateY(120vh);opacity:.1}}
@keyframes sfg-rise{0%{transform:translateY(0) scale(.6);opacity:0}15%{opacity:1}100%{transform:translateY(-120%);opacity:0}}
@keyframes sfg-stream-r{0%{transform:translateX(0);opacity:0}12%{opacity:.95}100%{transform:translateX(110vw);opacity:.12}}
@keyframes sfg-stream-l{0%{transform:translateX(0);opacity:0}12%{opacity:.95}100%{transform:translateX(-110vw);opacity:.12}}
@keyframes sfg-scroll-x{from{background-position:0 0}to{background-position:64px 0}}
@keyframes sfg-scroll-y{from{background-position:0 0}to{background-position:0 64px}}
@keyframes sfg-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
@keyframes sfg-ring{0%{width:8%;height:8%;margin:-4% 0 0 -4%;opacity:.8}100%{width:90%;height:90%;margin:-45% 0 0 -45%;opacity:0}}
@keyframes sfg-pulse{0%,100%{transform:scale(.72);opacity:.28}50%{transform:scale(1.12);opacity:.7}}
@media (prefers-reduced-motion:reduce){${sel} .sf-motion-host *,${sel}.sf-motion-host *{animation-duration:.01ms !important;animation-iteration-count:1 !important}}`);
  return parts.join("\n");
}

export function graphHostPlan(graph: CssGraph): Array<{ className: string; count?: number; alphabet?: string }> {
  const out: Array<{ className: string; count?: number; alphabet?: string }> = [];
  graph.ops.forEach((op, i) => {
    const n = i + 1;
    if (op.op === "fill") out.push({ className: `sf-g-fill-${n}` });
    if (op.op === "veil") out.push({ className: `sf-g-veil-${n}` });
    if (op.op === "flow") out.push({ className: `sf-g-flow-${n}` });
    if (op.op === "emit") out.push({ className: `sf-g-emit-${n}`, count: op.count });
    if (op.op === "lattice") out.push({ className: `sf-g-lat-${n}` });
    if (op.op === "burst") out.push({ className: `sf-g-burst-${n}` });
    if (op.op === "ripple") out.push({ className: `sf-g-rip-${n}`, count: op.count });
    if (op.op === "type") out.push({ className: "sf-fx-glyph", count: op.count, alphabet: op.alphabet });
    if (op.op === "grain") out.push({ className: `sf-g-grain-${n}` });
    if (op.op === "pulse") out.push({ className: `sf-g-pulse-${n}` });
  });
  return out;
}

export function graphSummary(graph: CssGraph): string {
  if (!graph.ops.length) return "still";
  return `${graph.ops.map((o) => o.op).join(" + ")} · ${graph.features.join(" ")}`;
}

export function inventIdFromPrompt(prompt: string): string {
  return slugifyId(prompt);
}
